import React, { ReactNode } from 'react';
import sanitizeHtml from 'sanitize-html';
import { isNil } from '../../../../lib/utils/isNil';
import styles from './BaseLearningDetails.module.scss';
import PureHeading from '../../../common/Heading/PureHeading';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import { classes } from '../../../../lib/utils/cssUtils';
import ErrorBoundary from '../../../common/ErrorBoundary/ErrorBoundary';
import { LearningDetailsProps } from '../../../../lib/types/aemDataModels';
import { Api } from '../../../../lib/types/apiSpec';
import LearningDetailsOrnaments from '../../../common/LearningDetailsOrnaments/LearningDetailsOrnaments';
import SkeletonBaseLearningDetails from './SkeletonBaseLearningDetails';
import { ALLOWED_RICHTEXT_TAGS } from '../../../../lib/constants/courses';
import PureRichText from '../../../common/PureRichText/PureRichText';
import { learningDetailsIds } from '../../../../lib/tests/testIds';

const BaseLearningDetails = <T extends Api.DocumentLibrary | Api.EventDetails>({
  nameLabel,
  descriptionLabel,
  elementsLabel,
  detailOptions,
  isLoading,
  data,
  error,
  button,
}: LearningDetailsProps<T>) => {
  if (isNil(data) && !isLoading && !error) {
    return null;
  }

  const dataId = `data-${data?.id}`;
  const nameId = `${dataId}-name`;
  const descriptionId = `${dataId}-description`;
  const detailsId = `${dataId}-details`;

  return (
    <SkeletonBaseLearningDetails isLoading={!!isLoading}>
      <ErrorBoundary error={error || !data}>
        <article className={styles.mainWrapper} aria-labelledby={nameId}>
          <div className={styles.stickyWrapper}>
            <div className={styles.stickyContainer}>
              <div className={styles.buttonContainer}>{button}</div>
            </div>
          </div>
          <section className={styles.rowBackgroundWrapper} aria-labelledby={nameId}>
            <div className={styles.rowGridContainer}>
              <div className={styles.rowGrid}>
                <div className={styles.leftColumn}>
                  <PureHeading level={HeadingLevel.h2} as={HeadingLevel.h3} className={styles.heading} id={nameId}>
                    {nameLabel}
                  </PureHeading>
                </div>
                <div className={styles.rightColumn} data-testid={learningDetailsIds.name}>
                  {data?.name}
                </div>
              </div>
            </div>
          </section>
          <section className={styles.rowBackgroundWrapper} aria-labelledby={descriptionId}>
            <div className={styles.rowGridContainer}>
              <div className={styles.rowGrid}>
                <div className={styles.leftColumn}>
                  <PureHeading
                    level={HeadingLevel.h2}
                    as={HeadingLevel.h3}
                    className={styles.heading}
                    id={descriptionId}
                  >
                    {descriptionLabel}
                  </PureHeading>
                </div>
                <div className={styles.rightColumn} data-testid={learningDetailsIds.description} role="region">
                  {!!data?.description && (
                    <PureRichText
                      richtext={sanitizeHtml(data.description, {
                        allowedTags: ALLOWED_RICHTEXT_TAGS,
                        allowedAttributes: false,
                      })}
                    />
                  )}
                </div>
              </div>
            </div>
          </section>
          <section className={styles.rowBackgroundWrapper} aria-labelledby={elementsLabel ? detailsId : undefined}>
            <div className={styles.rowGridContainer}>
              <div className={styles.rowGrid}>
                <div className={styles.leftColumn}>
                  <PureHeading level={HeadingLevel.h2} as={HeadingLevel.h3} className={styles.heading} id={detailsId}>
                    {elementsLabel}
                  </PureHeading>
                </div>
                <dl className={classes(styles.rightColumn, styles.elementsGrid)}>
                  {detailOptions?.map(option => (
                    <div
                      data-testid={option.property.toString()}
                      key={option.property.toString()}
                      className={styles.gridElement}
                    >
                      <dt className={styles.elementLabel}>{option.label}</dt>
                      <dd className={styles.elementValue}>{data?.[option.property] as ReactNode}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
            <LearningDetailsOrnaments />
          </section>
        </article>
      </ErrorBoundary>
    </SkeletonBaseLearningDetails>
  );
};

export default BaseLearningDetails;
